import { useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { UserPlus, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { registerAccount } from "@/lib/api";

// The ONE registration page in the whole product — there is no separate admin/owner
// signup page, setup key, or bootstrap step. Everyone (including the two MySiteCare
// owner accounts) creates a normal account here with their own chosen password; the
// backend alone decides — from the email alone — whether the account becomes an
// owner or an ordinary customer, and the response tells this page which session to
// start (see registerAccount() in @/lib/api and register_account() in server.py).
export default function Register() {
  const adminAuth = useAdminAuth();
  const customerAuth = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedReturn = params.get("return_to");
  const returnTo = requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : window.sessionStorage.getItem("mysitecare-checkout-intent");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (adminAuth.isLoggedIn) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (customerAuth.isLoggedIn) {
    return <Navigate to={returnTo || "/account/dashboard"} replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await registerAccount(email, password, displayName);
      if (res.kind === "owner") {
        adminAuth.setSession(res.token, res.email);
        navigate("/admin/dashboard", { replace: true });
      } else {
        customerAuth.setSession(res.token);
        navigate(returnTo || "/account/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create the account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#F8FAFC] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2.5 text-[#10B981] mb-6">
          <ShieldCheck size={22} />
          <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#0F172A]">Create Account</span>
        </div>
        <h1 className="text-2xl font-bold font-display text-[#0F172A]">Create your MySiteCare account</h1>
        <p className="mt-2 text-sm text-[#475569]">Have a Website Access Code from MySiteCare? Create your account first, then add your website from your dashboard.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="register-name" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Your name or business name (optional)</label>
            <input id="register-name" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="register-name-input"
              className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm outline-none focus:border-[#10B981]" />
          </div>
          <div>
            <label htmlFor="register-email" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Email</label>
            <input id="register-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input"
              className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm outline-none focus:border-[#10B981]" />
          </div>
          <div>
            <label htmlFor="register-password" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Choose a password</label>
            <div className="relative mt-1.5"><input id="register-password" type={showPassword ? "text" : "password"} required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-[#E2E8F0] px-4 py-2.5 pr-12 text-sm outline-none focus:border-[#10B981]" data-testid="register-password-input" /><button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="register-password-visibility-toggle">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
          </div>
          <div>
            <label htmlFor="register-password-confirmation" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Confirm password</label>
            <input id="register-password-confirmation" type={showPassword ? "text" : "password"} required minLength={8} autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm outline-none focus:border-[#10B981]" data-testid="register-password-confirmation-input" />
          </div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <button type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F172A] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            <UserPlus size={15} /> {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-[#475569]">
          Already have an account? <Link to={`/account/login${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ""}`} className="font-semibold text-[#10B981] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
